import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, RotateCcw } from "lucide-react";
import { AppButton, ConfirmationModal } from "@libs/zapi-ui/custom";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";

import { getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { useProject } from "@/zapi-frontend/pages/UpdateProjectPage/model/useProject";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useChatUnreadIndicator } from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";

import { CaseBriefEditor } from "@/zapi-frontend/widgets/CreateProjectChat/ui/CaseBriefEditor";
import { EditProjectChat } from "@/zapi-frontend/widgets/EditProjectChat";
import { InstructionsModal } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsModal";
import { ResponsiveChatPanel } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { UnsavedChangesDialog } from "@/zapi-frontend/shared/ui/UnsavedChangesDialog";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { projectPageBackgroundStyle } from "@/zapi-frontend/shared/lib/projectTheme";
import { useProjectLanguageSync } from "@/zapi-frontend/shared/i18n/useProjectLanguageSync";
import $api from "@/zapi-frontend/shared/api/axios";
import type { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";

type ProjectFormState = {
  name: string;
  description: string;
  language: string;
  visibility: "organization" | "private";
  projectInstructions: string;
  chaptersInstructions: Record<string, string>;
  presentationPrompt: string;
  dimensionValues: Record<string, string>;
};

function caseBriefContentSignature(md: string): string {
  return md
    .replace(/<[^>]*>/g, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^\s*([-*+]|\d+\.)\s+/gm, "")
    .replace(/[*_~`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildInitialState(): ProjectFormState {
  return {
    name: "",
    description: "",
    language: "english",
    visibility: "organization",
    projectInstructions: "",
    chaptersInstructions: {},
    presentationPrompt: "",
    dimensionValues: {}
  };
}

const triggerAutoSetup = async (projectId: string, model?: string, prefill = true): Promise<number | null> => {
  try {
    const response = await $api.post<{ taskId: number }>(`/projects/${projectId}/auto-setup`, {
      model,
      prefill
    });
    return response.data.taskId;
  } catch (error) {
    console.error("Failed to trigger auto-setup:", error);
    throw error;
  }
};

export function UpdateProjectPage() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { modelName: selectedModel } = useChatStore();
  const { setSetupTask } = useProjectSetupStore();
  const { updateProject } = useProjectStore();
  const { data: orgDimensions = [] } = useProjectDimensions();

  const { project, loading, refetch, optimisticRename } = useProject(id || null);
  const { documents, createDocument, createWebLink, deleteDocument, refetchDocuments } = useDocuments(
    id || "",
    undefined,
    3000
  );

  const [caseBriefMdContent, setCaseBriefMdContent] = useState("");
  const [formState, setFormState] = useState<ProjectFormState>(buildInitialState());

  useProjectLanguageSync(formState.language);
  const [isDirty, setIsDirty] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isRelaunchOpen, setIsRelaunchOpen] = useState(false);
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);
  const [chatAssistantCount, setChatAssistantCount] = useState<number | null>(null);
  const { hasUnread: hasUnreadChat, markSeen: markChatSeen } = useChatUnreadIndicator(chatAssistantCount ?? 0, {
    ready: chatAssistantCount !== null,
    firstVisitKey: id ? `project-edit:${id}` : undefined
  });

  const initialSnapshotRef = useRef<{ caseBriefMdContent: string; form: ProjectFormState } | null>(null);
  const savedRef = useRef<{ name: string; description: string; caseBriefMd: string } | null>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!project) return;
    const nextForm: ProjectFormState = {
      name: project.name,
      description: project.shortDescription || "",
      language: project.language || "english",
      visibility: project.visibility ?? "organization",
      projectInstructions: project.projectInstructions || "",
      chaptersInstructions: project.projectChapters.reduce((acc, chapter) => {
        acc[chapter.projectTemplateChapter.slug] = chapter.chapterInstructions ?? "";
        return acc;
      }, {} as Record<string, string>),
      presentationPrompt: project.presentationPrompt || "",
      dimensionValues: project.dimensionValues ?? {}
    };
    const nextCaseBrief = project.caseBriefMdContent || "";
    setFormState(nextForm);
    setCaseBriefMdContent(nextCaseBrief);
    initialSnapshotRef.current = { caseBriefMdContent: nextCaseBrief, form: nextForm };
    savedRef.current = { name: nextForm.name, description: nextForm.description, caseBriefMd: nextCaseBrief };
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    if (!initialSnapshotRef.current) return;
    const initial = initialSnapshotRef.current;
    const sameBrief =
      caseBriefContentSignature(caseBriefMdContent) === caseBriefContentSignature(initial.caseBriefMdContent);
    const sameForm =
      formState.language === initial.form.language &&
      formState.projectInstructions === initial.form.projectInstructions &&
      formState.presentationPrompt === initial.form.presentationPrompt &&
      JSON.stringify(formState.chaptersInstructions) === JSON.stringify(initial.form.chaptersInstructions) &&
      JSON.stringify(formState.dimensionValues) === JSON.stringify(initial.form.dimensionValues);
    setIsDirty(!(sameBrief && sameForm));
  }, [caseBriefMdContent, formState]);

  const autosaveDetails = useCallback(
    async (next: { name: string; description: string; caseBriefMd: string }) => {
      const saved = savedRef.current;
      const initial = initialSnapshotRef.current;
      if (!id || !saved || !initial) return;

      const payload: { name?: string; shortDescription?: string; caseBriefMdContent?: string } = {};
      if (next.name !== saved.name && next.name.trim()) payload.name = next.name;
      if (next.description !== saved.description) payload.shortDescription = next.description;
      const briefChanged = next.caseBriefMd !== saved.caseBriefMd;
      const isFormattingOnly =
        caseBriefContentSignature(next.caseBriefMd) === caseBriefContentSignature(initial.caseBriefMdContent);
      if (briefChanged && isFormattingOnly) payload.caseBriefMdContent = next.caseBriefMd;

      if (Object.keys(payload).length === 0) return;

      autosaveAbortRef.current?.abort();
      const controller = new AbortController();
      autosaveAbortRef.current = controller;
      try {
        await $api.patch(`/projects/${id}/details`, payload, { signal: controller.signal });
        if (controller.signal.aborted) return;
        savedRef.current = {
          name: payload.name !== undefined ? next.name : saved.name,
          description: payload.shortDescription !== undefined ? next.description : saved.description,
          caseBriefMd: payload.caseBriefMdContent !== undefined ? next.caseBriefMd : saved.caseBriefMd
        };
        if (payload.name !== undefined || payload.shortDescription !== undefined) {
          if (initialSnapshotRef.current) {
            if (payload.name !== undefined) initialSnapshotRef.current.form.name = next.name;
            if (payload.shortDescription !== undefined) initialSnapshotRef.current.form.description = next.description;
          }
          const { selectedProject, setSelectedProject } = useProjectStore.getState();
          if (selectedProject?.id === id) {
            setSelectedProject({
              ...selectedProject,
              ...(payload.name !== undefined ? { name: next.name } : {}),
              ...(payload.shortDescription !== undefined ? { shortDescription: next.description } : {})
            });
          }
          await queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });
        }
        toast.success("Changes savedddd");
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to autosave project details:", error);
        toast.error("Failed to save changes. Please try again.");
      }
    },
    [id, queryClient]
  );

  useEffect(() => {
    if (!id || !savedRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    const snapshot = { name: formState.name, description: formState.description, caseBriefMd: caseBriefMdContent };
    autosaveTimerRef.current = setTimeout(() => void autosaveDetails(snapshot), 500);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [formState.name, formState.description, caseBriefMdContent, id, autosaveDetails]);

  const sortedDimensions = useMemo(() => [...orgDimensions].sort((a, b) => a.sortOrder - b.sortOrder), [orgDimensions]);

  const vaultDocuments: TPreUploadedDocument[] = useMemo(
    () =>
      documents.map((d) => ({
        documentId: d.id,
        fileName: d.fileName,
        fileSize: d.fileSize,
        webUrl: d.webUrl ?? undefined,
        status: d.status
      })),
    [documents]
  );

  const isTemplateLess = project?.projectTemplateSlug === "";

  const chaptersForModal = useMemo(() => {
    if (!project || isTemplateLess) return [];
    return project.projectChapters
      .map((chapter) => ({
        slug: chapter.projectTemplateChapter.slug,
        name: chapter.projectTemplateChapter.name,
        icon: chapter.projectTemplateChapter.icon
      }))
      .filter((c) => !!c.slug);
  }, [project, isTemplateLess]);

  const handleSaveAndRelaunch = useCallback(async () => {
    if (!id) return;
    setIsLaunching(true);
    try {
      await updateProject(id, {
        name: formState.name,
        description: formState.description,
        language: formState.language,
        visibility: formState.visibility,
        projectInstructions: formState.projectInstructions,
        ...(isTemplateLess ? {} : { chaptersInstructions: formState.chaptersInstructions }),
        presentationPrompt: formState.presentationPrompt,
        dimensionValues: formState.dimensionValues,
        caseBriefMdContent
      });
      await queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });

      const response = await $api.post<{ data: { id: number } }>(`/projects/${id}/duplicate-inputs`, {});
      const targetId = String(response.data.data.id);

      const taskId = await triggerAutoSetup(targetId, selectedModel, true);
      if (taskId) setSetupTask(targetId, taskId);

      navigate(getRouteProjectDetails(targetId));
    } catch (error) {
      console.error("Failed to relaunch project:", error);
      toast.error("Failed to relaunch project. Please try again.");
    } finally {
      setIsLaunching(false);
      setIsRelaunchOpen(false);
    }
  }, [
    id,
    formState,
    caseBriefMdContent,
    updateProject,
    queryClient,
    selectedModel,
    setSetupTask,
    navigate,
    isTemplateLess
  ]);

  const handleBackClick = useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    void autosaveDetails({ name: formState.name, description: formState.description, caseBriefMd: caseBriefMdContent });
    if (isDirty) {
      setIsLeaveOpen(true);
      return;
    }
    if (id) navigate(getRouteProjectDetails(id));
  }, [isDirty, id, navigate, formState.name, formState.description, caseBriefMdContent, autosaveDetails]);

  const handleLeaveWithoutSaving = useCallback(() => {
    setIsLeaveOpen(false);
    if (id) navigate(getRouteProjectDetails(id));
  }, [id, navigate]);

  const handleSaveAndLeave = useCallback(async () => {
    if (!id) return;
    setIsLaunching(true);
    try {
      await updateProject(id, {
        name: formState.name,
        description: formState.description,
        language: formState.language,
        visibility: formState.visibility,
        projectInstructions: formState.projectInstructions,
        ...(isTemplateLess ? {} : { chaptersInstructions: formState.chaptersInstructions }),
        presentationPrompt: formState.presentationPrompt,
        dimensionValues: formState.dimensionValues,
        caseBriefMdContent
      });
      await queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });
      navigate(getRouteProjectDetails(id));
    } catch (error) {
      console.error("Failed to save changes:", error);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setIsLaunching(false);
      setIsLeaveOpen(false);
    }
  }, [id, formState, caseBriefMdContent, updateProject, queryClient, navigate, isTemplateLess]);

  const handleInstructionsSave = useCallback(() => {
    setIsInstructionsOpen(false);
  }, []);

  const patchInstructions = useCallback((projectInstructions: string, chaptersInstructions: Record<string, string>) => {
    setFormState((prev) => ({ ...prev, projectInstructions, chaptersInstructions }));
  }, []);

  const patchPresentationPrompt = useCallback((value: string) => {
    setFormState((prev) => ({ ...prev, presentationPrompt: value }));
  }, []);

  if (loading) {
    return <PageLoader className="min-h-screen bg-innoveraZinc-100" />;
  }

  if (!project || !id) {
    return null;
  }

  const saveDisabled = !isDirty || isLaunching || !hasEdit;

  return (
    <div
      className={cn("flex flex-col bg-projectPage h-[calc(100vh-56px)] w-full overflow-hidden")}
      style={projectPageBackgroundStyle(project.iconColor)}
    >
      <div className="flex gap-2 flex-1 min-h-0 p-2">
        <div className="flex flex-col flex-1 bg-white overflow-hidden min-w-0">
          <div className="bg-white border-b border-innoveraZinc-100 flex items-center justify-between px-4 py-3 shrink-0">
            <div className="flex flex-1 items-center gap-1 min-w-0">
              <AppButton
                data-testid="update-project-back-button"
                appearance="text"
                onClick={handleBackClick}
                startAdornment={<ChevronLeft className="size-4" />}
                className="hidden hd:inline-flex"
              >
                Back to Project
              </AppButton>
              <AppButton
                data-testid="update-project-back-icon-button"
                appearance="text"
                size="icon"
                onClick={handleBackClick}
                aria-label="Back to Project"
                startAdornment={<ChevronLeft className="size-4" />}
                className="hd:hidden"
              />
              <p className="hd:hidden text-[18px] font-medium leading-7 text-innoveraZinc-900 tracking-[-0.18px] min-w-0 truncate">
                Edit Project
              </p>
            </div>

            <p className="hidden hd:block flex-1 text-[18px] font-medium leading-7 text-center text-innoveraZinc-900 tracking-[-0.18px] min-w-0 truncate">
              Edit Project
            </p>

            <div className="flex flex-1 items-center justify-end gap-3 min-w-0">
              <AppButton
                data-testid="update-project-relaunch-button"
                color="zinc950"
                disabled={saveDisabled}
                isLoading={isLaunching}
                onClick={() => setIsRelaunchOpen(true)}
                startAdornment={<RotateCcw className="size-4" />}
              >
                Relaunch Analysis
              </AppButton>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            <CaseBriefEditor
              content={caseBriefMdContent}
              onUpdate={setCaseBriefMdContent}
              projectName={formState.name}
              projectDescription={formState.description}
              onProjectNameChange={(name) => setFormState((p) => ({ ...p, name }))}
              onProjectDescriptionChange={(description) => setFormState((p) => ({ ...p, description }))}
              visibility={formState.visibility}
              visibilityActive
              onVisibilityChange={async (visibility) => {
                const previous = formState.visibility;
                if (visibility === previous) return;
                setFormState((p) => ({ ...p, visibility }));
                try {
                  await updateProject(id, { visibility });
                  toast.success(
                    visibility === "private" ? "Project is now private" : "Project is now visible to the organization"
                  );
                } catch {
                  setFormState((p) => ({ ...p, visibility: previous }));
                  toast.error("Failed to update visibility");
                }
              }}
              showInstructionsChip
              onInstructionsClick={() => setIsInstructionsOpen(true)}
              vaultActive
              vaultDocuments={vaultDocuments}
              onVaultAddFiles={async (files) => {
                for (const file of Array.from(files)) {
                  await createDocument(file);
                }
                await refetchDocuments();
              }}
              onVaultAddWebLink={async (url) => {
                await createWebLink(url);
                await refetchDocuments();
              }}
              onVaultRemove={async (documentId) => {
                await deleteDocument(documentId);
                await refetchDocuments();
              }}
              dimensions={sortedDimensions}
              dimensionValues={formState.dimensionValues}
              onDimensionChange={(dimensionId, value) =>
                setFormState((prev) => {
                  const next = { ...(prev.dimensionValues ?? {}) };
                  if (value.trim() === "") delete next[String(dimensionId)];
                  else next[String(dimensionId)] = value;
                  return { ...prev, dimensionValues: next };
                })
              }
              versionDisplayName={project.versionDisplayName || undefined}
              versionHistory={
                project.organizationId != null
                  ? {
                      rowProjectId: String(project.id),
                      projectName: project.name,
                      projectLogoUrl: project.logoUrl,
                      projectIconShape: project.iconShape,
                      projectIconColor: project.iconColor,
                      versions: project.versions ?? [],
                      hasEdit,
                      organizationId: project.organizationId,
                      onInvalidated: () => void refetch({ silent: true }),
                      onOptimisticRename: optimisticRename
                    }
                  : undefined
              }
              caseBriefId={project.caseBriefId}
              projectId={project.id}
            />
          </div>
        </div>

        <ResponsiveChatPanel hasUnread={hasUnreadChat} onOpenChange={markChatSeen}>
          <EditProjectChat
            projectId={Number(id)}
            language={formState.language}
            onDocumentUpdate={setCaseBriefMdContent}
            onAssistantMessageCountChange={setChatAssistantCount}
            onUploadFiles={async (files) => {
              for (const file of files) {
                await createDocument(file);
              }
              await refetchDocuments();
            }}
            onAddWebLink={async (url) => {
              await createWebLink(url);
              await refetchDocuments();
            }}
          />
        </ResponsiveChatPanel>
      </div>

      <InstructionsModal
        open={isInstructionsOpen}
        onClose={() => setIsInstructionsOpen(false)}
        onSave={handleInstructionsSave}
        chapters={chaptersForModal}
        projectInstructions={formState.projectInstructions}
        chaptersInstructions={formState.chaptersInstructions}
        presentationPrompt={formState.presentationPrompt}
        onProjectInstructionsChange={patchInstructions}
        onPresentationPromptChange={patchPresentationPrompt}
      />

      <ConfirmationModal
        isOpen={isRelaunchOpen}
        onClose={() => setIsRelaunchOpen(false)}
        onConfirm={handleSaveAndRelaunch}
        title={t("projectHome:caseFrame.relaunch")}
        description={t("projectHome:caseFrame.relaunchDescription")}
        confirmText={t("projectHome:caseFrame.relaunchConfirm")}
        cancelText={t("common:cancel")}
        confirmButtonColor="zinc950"
      />

      <UnsavedChangesDialog
        open={isLeaveOpen}
        isSaving={isLaunching}
        onClose={() => setIsLeaveOpen(false)}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        onSaveAndLeave={() => void handleSaveAndLeave()}
      />
    </div>
  );
}
